/**
 * videoCoherence.js — "request ↔ VAST coherence" layer of the Video
 * Implementation Passport.
 *
 * Compares what a video ad request ASKED FOR (GAM video ad-tag params /
 * ORTB video imp object) against what the VAST response actually
 * DELIVERED (parsed creative/mediaFiles/verifications). This is the
 * tool's unique edge — it sees both sides of the ad call.
 *
 * Pure module — no React/DOM — unit-tested in node. Mirrors the project's
 * validator convention: never throw, degrade gracefully (skip a check
 * rather than emit a bad row; return null when there is nothing to show).
 */

const VIDEO_TYPES = new Set(['gam_video', 'ima', 'amazon_video', 'springserve_ad_request']);
const ORTB_TYPES = new Set(['amazon_video', 'springserve_ad_request']);

// Protocol code (ORTB imp.protocols[].code) → VAST major version it implies.
const PROTOCOL_TO_VAST_MAJOR = { 1: 2, 2: 2, 3: 3, 4: 2, 5: 3, 6: 3, 7: 4, 8: 4 };

/** Parse a URL's query string safely. Returns null (never throws) if unparsable/missing. */
function safeParams(url) {
  if (!url) return null;
  try {
    return new URL(url).searchParams;
  } catch {
    return null;
  }
}

function get(params, key) {
  if (!params) return null;
  const v = params.get(key);
  return v === '' ? null : v;
}

function check(key, label, state, requested, delivered, note) {
  return { key, label, requested, delivered, state, note: note || '' };
}

/** Parse the leading integer of a VAST version string, e.g. '4.1' → 4. NaN if unparsable. */
function vastMajor(version) {
  if (version === null || version === undefined) return NaN;
  const m = String(version).match(/^\s*(\d+)/);
  return m ? Number(m[1]) : NaN;
}

/**
 * Compare a video ad request against its delivered VAST response.
 *
 * @param {{ requestType?: string, type?: string, url?: string, validData?: object }} row
 * @returns {null | {
 *   hasVast: boolean,
 *   checks: Array<{ key: string, label: string, requested: string, delivered: string,
 *                    state: 'pass'|'warn'|'fail'|'info', note: string }>,
 *   verdict: { level: 'pass'|'warn'|'fail', flagged: number }
 * }}
 */
export function videoCoherence(row) {
  if (!row || typeof row !== 'object') return null;

  const type = row.requestType || row.type;
  if (!VIDEO_TYPES.has(type)) return null;

  const validData = row.validData || {};
  const vast = validData.vastResponse || validData.vast || null;
  if (!vast) return null;

  const checks = [];

  // -------------------------------------------------------------------
  // 1. Fill
  // -------------------------------------------------------------------
  const isNoFill = vast.filled === false || vast.adCount === 0 || vast.empty === true;
  if (isNoFill) {
    checks.push(
      check(
        'fill',
        'Fill',
        'warn',
        '1 ad expected',
        `empty VAST${vast.error ? ` · ${vast.error}` : ''}`,
        'no-fill — expected on unsold inventory; investigate if persistent for this slot'
      )
    );
    return { hasVast: true, checks, verdict: { level: 'warn', flagged: 1 } };
  }

  const creative = vast.creative || null;
  const isOrtb = ORTB_TYPES.has(type);
  const imp = isOrtb ? validData.videoOrtb?.imps?.[0] : null;
  const params = !isOrtb ? safeParams(row.url) : null;

  // -------------------------------------------------------------------
  // 2. Duration overrun
  // -------------------------------------------------------------------
  if (creative && creative.durationSeconds != null) {
    let reqMax = null;
    if (isOrtb) {
      if (imp && imp.maxduration != null) reqMax = Number(imp.maxduration);
    } else if (params) {
      const pmxd = Number(get(params, 'pmxd'));
      const maxAdDuration = Number(get(params, 'max_ad_duration'));
      reqMax = !Number.isNaN(pmxd) && pmxd ? pmxd : (!Number.isNaN(maxAdDuration) && maxAdDuration ? maxAdDuration : null);
    }

    if (reqMax != null && !Number.isNaN(reqMax) && reqMax > 0) {
      if (creative.durationSeconds > reqMax) {
        checks.push(
          check(
            'duration',
            'Duration',
            'fail',
            `max ${reqMax}s`,
            `${creative.durationSeconds}s`,
            'creative exceeds slot max — player may cut off'
          )
        );
      } else {
        checks.push(
          check('duration', 'Duration', 'pass', `max ${reqMax}s`, `${creative.durationSeconds}s`, 'within bound')
        );
      }
    }
  }

  // -------------------------------------------------------------------
  // 3. Skip coherence — ORTB only
  // -------------------------------------------------------------------
  if (isOrtb && imp && imp.skip != null) {
    const reqSkippable = imp.skip === 1;
    const creativeSkippable = !!(creative && (creative.skippable === true || creative.skipOffset != null));

    if (reqSkippable && !creativeSkippable) {
      checks.push(
        check(
          'skip',
          'Skip coherence',
          'fail',
          'skip=1 (skippable)',
          'no skipoffset',
          'viewer cannot skip a slot marked skippable'
        )
      );
    } else if (!reqSkippable && creativeSkippable) {
      checks.push(
        check(
          'skip',
          'Skip coherence',
          'info',
          'skip=0',
          'skipoffset present',
          'creative is skippable though the slot is not'
        )
      );
    } else {
      checks.push(
        check(
          'skip',
          'Skip coherence',
          'pass',
          `skip=${imp.skip}`,
          creativeSkippable ? 'skippable' : 'non-skippable',
          'consistent'
        )
      );
    }
  }

  // -------------------------------------------------------------------
  // 4. Protocol
  // -------------------------------------------------------------------
  {
    const deliveredMajor = vastMajor(vast.version);
    if (!Number.isNaN(deliveredMajor)) {
      let allowed = null; // Set<number> | null (null = skip check)
      let reqLabel = null;

      if (isOrtb) {
        if (Array.isArray(imp?.protocols) && imp.protocols.length) {
          allowed = new Set();
          imp.protocols.forEach((p) => {
            const code = p && typeof p === 'object' ? p.code : p;
            if (code != null && PROTOCOL_TO_VAST_MAJOR[code] != null) {
              allowed.add(PROTOCOL_TO_VAST_MAJOR[code]);
            }
          });
          if (!allowed.size) allowed = null;
          reqLabel = allowed ? `VAST ${[...allowed].join('/')}` : null;
        }
      } else if (params) {
        const output = get(params, 'output') || '';
        if (/xml_vast4/i.test(output)) {
          allowed = new Set([4]);
          reqLabel = 'VAST 4';
        } else if (/xml_vast3/i.test(output)) {
          allowed = new Set([3]);
          reqLabel = 'VAST 3';
        } else if (/xml_vast2/i.test(output)) {
          allowed = new Set([2]);
          reqLabel = 'VAST 2';
        }
        // plain 'vast'/'vmap'/other → allowed stays null (skip, any version ok)
      }

      if (allowed && allowed.size) {
        if (!allowed.has(deliveredMajor)) {
          checks.push(
            check(
              'protocol',
              'Protocol',
              'warn',
              reqLabel,
              `VAST ${vast.version}`,
              'returned VAST version not in the requested set'
            )
          );
        } else {
          checks.push(check('protocol', 'Protocol', 'pass', reqLabel, `VAST ${vast.version}`, 'consistent'));
        }
      }
    }
  }

  // -------------------------------------------------------------------
  // 5. API / render
  // -------------------------------------------------------------------
  if (creative) {
    let reqOmid = false;
    if (isOrtb) {
      reqOmid = !!(imp && imp.hasOmid);
    } else if (params) {
      const omidP = get(params, 'omid_p');
      const omidV = get(params, 'omid_v');
      const sdkApis = get(params, 'sdk_apis');
      const sdkApisList = sdkApis ? sdkApis.split('_') : [];
      reqOmid = !!(omidP || omidV) || sdkApisList.includes('7');
    }

    const creativeVpaid = creative.vpaid === true;
    const creativeHasOmidNode =
      creative.hasOmid === true || (Array.isArray(creative.verifications) && creative.verifications.length > 0);

    if (creativeVpaid && (!isOrtb || (imp && imp.hasVpaid === false))) {
      checks.push(
        check('api', 'API / render', 'warn', 'OMID / non-VPAID', 'VPAID creative', 'VPAID deprecated; no OMID measurement path')
      );
    } else if (reqOmid && !creativeHasOmidNode) {
      checks.push(
        check(
          'api',
          'API / render',
          'warn',
          'OMID (7)',
          'no verification node',
          'viewability measurement requested but creative ships no OMID/AdVerifications'
        )
      );
    } else {
      checks.push(
        check(
          'api',
          'API / render',
          'pass',
          'OMID (7)',
          creativeHasOmidNode ? 'OMID present' : '—',
          'measurable'
        )
      );
    }
  }

  // -------------------------------------------------------------------
  // 6. MIME — ORTB only
  // -------------------------------------------------------------------
  if (isOrtb && imp && Array.isArray(imp.mimes) && imp.mimes.length && Array.isArray(creative?.mediaFiles) && creative.mediaFiles.length) {
    const reqMimes = imp.mimes.map((m) => String(m).toLowerCase());
    const deliveredMimes = creative.mediaFiles
      .map((m) => String((m && m.mime) || '').toLowerCase())
      .filter(Boolean);

    if (deliveredMimes.length) {
      const overlap = deliveredMimes.some((m) => reqMimes.includes(m));
      if (!overlap) {
        const vpaidOnly = deliveredMimes.every((m) => m === 'application/javascript');
        const reqHasMp4 = reqMimes.some((m) => m.includes('mp4'));
        const note =
          vpaidOnly && reqHasMp4
            ? 'VPAID-only, no mp4 fallback'
            : 'no deliverable rendition matches the requested mimes';
        checks.push(
          check('mime', 'MIME', 'fail', reqMimes.join(', '), deliveredMimes.join(', '), note)
        );
      } else {
        checks.push(check('mime', 'MIME', 'pass', reqMimes.join(', '), deliveredMimes.join(', '), 'match found'));
      }
    }
  }

  // -------------------------------------------------------------------
  // Verdict
  // -------------------------------------------------------------------
  let level = 'pass';
  let flagged = 0;
  checks.forEach((c) => {
    if (c.state === 'fail') {
      level = 'fail';
      flagged += 1;
    } else if (c.state === 'warn') {
      if (level !== 'fail') level = 'warn';
      flagged += 1;
    }
  });

  return { hasVast: true, checks, verdict: { level, flagged } };
}

export default videoCoherence;
